import React, { useCallback, useEffect, useState } from 'react';
import { Select, Button, Space, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

function authHeaders() {
  const token = localStorage.getItem('token') || '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** 從多種後端回應格式取出陣列。 */
function extractArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.records)) return data.records;
  return [];
}

/** 各步驟的資料來源設定：API、id/label 欄位。 */
const SOURCES = {
  template: {
    multiple: false,
    fetch: async () => {
      const urls = [
        '/api/whatsapptemplates?templateSource=internal&page=1&pageSize=100',
        '/api/whatsapptemplates?templateSource=meta&page=1&pageSize=100',
      ];
      const all = [];
      for (const url of urls) {
        try {
          const res = await fetch(url, { headers: authHeaders() });
          if (!res.ok) continue;
          const json = await res.json();
          extractArray(json).forEach((r) => all.push(r));
        } catch {
          /* ignore */
        }
      }
      return all;
    },
    toOption: (r) => ({
      value: String(r.id ?? r.Id),
      label: `${r.name ?? r.Name ?? '(未命名)'}${(r.templateSource ?? r.TemplateSource) ? ` · ${r.templateSource ?? r.TemplateSource}` : ''}`,
    }),
  },
  dataset: {
    multiple: false,
    fetch: async () => {
      const res = await fetch('/api/datasets?page=1&pageSize=100', { headers: authHeaders() });
      if (!res.ok) return [];
      return extractArray(await res.json());
    },
    toOption: (r) => ({ value: String(r.id ?? r.Id), label: r.name ?? r.Name ?? '(未命名)' }),
  },
  aiStaff: {
    multiple: false,
    fetch: async () => {
      const res = await fetch('/api/aivirtualemployees?page=1&pageSize=100', { headers: authHeaders() });
      if (!res.ok) return [];
      return extractArray(await res.json());
    },
    toOption: (r) => ({
      value: String(r.id ?? r.Id),
      label: [r.displayName ?? r.DisplayName, r.jobTitle ?? r.JobTitle].filter(Boolean).join(' · ') || '(未命名)',
    }),
  },
  kb: {
    multiple: true,
    fetch: async () => {
      const res = await fetch('/api/knowledge-documents?page=1&pageSize=200', { headers: authHeaders() });
      if (!res.ok) return [];
      return extractArray(await res.json());
    },
    toOption: (r) => ({ value: String(r.id ?? r.Id), label: r.title ?? r.Title ?? '(未命名)' }),
  },
};

/**
 * 建置嚮導的「選用此資源」選擇器。純粹讀取列表 + 記住選項，
 * 不會修改內嵌頁面本身的獨立功能。
 */
const BuildAppResourcePicker = ({ stepKey, value, onChange, defaultId, labels }) => {
  const source = SOURCES[stepKey];
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!source) return;
    setLoading(true);
    try {
      const rows = await source.fetch();
      setOptions(rows.map(source.toOption).filter((o) => o.value && o.value !== 'undefined'));
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => {
    load();
  }, [load]);

  // 自動選用剛在生成階段建立的資源（僅單選、且尚未選擇時）
  useEffect(() => {
    if (!source || source.multiple) return;
    if (!value && defaultId) onChange(String(defaultId));
  }, [source, value, defaultId, onChange]);

  if (!source) return null;

  const selectedCount = source.multiple ? (value?.length || 0) : (value ? 1 : 0);

  return (
    <div style={styles.bar}>
      <Space size="small" wrap style={{ flex: 1 }}>
        <span style={styles.label}>{labels?.pick || '選用：'}</span>
        <Select
          mode={source.multiple ? 'multiple' : undefined}
          allowClear
          showSearch
          optionFilterProp="label"
          loading={loading}
          value={source.multiple ? (value || []) : (value || undefined)}
          onChange={onChange}
          options={options}
          placeholder={labels?.placeholder || '選擇要用於流程的項目'}
          style={{ minWidth: 320, maxWidth: 560 }}
        />
        {selectedCount > 0 && (
          <Tag color="purple">{(labels?.selected || '已選用')} {selectedCount}</Tag>
        )}
      </Space>
      <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
        {labels?.refresh || '重新整理'}
      </Button>
    </div>
  );
};

const styles = {
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 24px',
    background: '#fff',
    borderBottom: '1px solid #f0f0f0',
    flexShrink: 0,
  },
  label: { color: '#555', fontSize: 13, fontWeight: 600 },
};

export default BuildAppResourcePicker;
